import meKeys from '@/api/queries/me/keyFactory';
import { GetMe } from '@/api/queries/me/useGetMe';
import { gqlClient } from '@/api/graphql-client';
import { useQuery } from '@tanstack/react-query';
import { minutesToMilliseconds } from 'date-fns';
import { useCallback, useMemo } from 'react';

// Shares the GetMe cache entry, but subscribes only to the permissions array
// so background refetches don't notify every guard on fetch-state changes.
const useMyPermissions = () => {
  const { data, isLoading } = useQuery({
    queryKey: meKeys.all,
    queryFn: async () => gqlClient.request(GetMe),
    staleTime: minutesToMilliseconds(5),
    select: (d) => d.me.permissions,
  });
  return { permissions: data, isLoading };
};

/**
 * Permission checks against the resolved permission list from the me query
 * (undefined while loading, so checks return false until it settles).
 *
 * The all-of check requires each permission to be present in the resolved
 * list, matching how the backend grants permissions (any one mapped role).
 * This deliberately replaces the old client-side check that required every
 * mapped role per permission.
 */
export const usePermissions = () => {
  const { permissions, isLoading } = useMyPermissions();

  const userHasAllRequiredPermissions = useCallback(
    (required: string[]) =>
      required.every((permission) => !!permissions?.includes(permission)),
    [permissions]
  );

  const userHasRequiredPermission = useCallback(
    (required: string[]) =>
      required.some((permission) => !!permissions?.includes(permission)),
    [permissions]
  );

  return {
    userHasAllRequiredPermissions,
    userHasRequiredPermission,
    isLoading,
  };
};

export interface IUseUserHasPermissionArgs {
  permissions?: string[];
  requiresAllPermissions?: boolean;
}

export const useUserHasPermission = (args: IUseUserHasPermissionArgs) => {
  const { permissions, requiresAllPermissions = false } = args;

  const { userHasAllRequiredPermissions, userHasRequiredPermission } =
    usePermissions();

  return useMemo(() => {
    let result = true;

    if (permissions?.length) {
      result = requiresAllPermissions
        ? userHasAllRequiredPermissions(permissions)
        : userHasRequiredPermission(permissions);
    }

    return result;
  }, [
    userHasAllRequiredPermissions,
    userHasRequiredPermission,
    permissions,
    requiresAllPermissions,
  ]);
};
